import React, { useEffect, useState } from "react";
import { Badge, Button, Spinner } from "@/components/ui";
import { endpoints, apiError } from "@/lib/api";
import { fmt } from "@/lib/format";
import { Sparkles, X, Mail } from "lucide-react";
import { TeaserModal } from "@/components/TeaserModal";

export function ScoreBar({ score }) {
  const col = score >= 75 ? "#10b981" : score >= 55 ? "#C5A059" : "#64748b";
  return (
    <div className="flex items-center gap-2">
      <div className="w-20 h-1.5 rounded bg-white/10 overflow-hidden"><div className="h-full" style={{ width: `${score}%`, background: col }} /></div>
      <span className="font-mono text-[13px]" style={{ color: col }}>{score}</span>
    </div>
  );
}

export function Breakdown({ b }) {
  return (
    <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-2">
      {Object.entries(b).map(([k, v]) => (
        <div key={k} className="text-[11px] text-white/55"><span className="text-white/80 capitalize">{k}</span> {v.points}/{v.max} · {v.reason}</div>
      ))}
    </div>
  );
}

function MatchRow({ m, dealId }) {
  const [open, setOpen] = useState(false);
  const [expl, setExpl] = useState(m.explanation || "");
  const [busy, setBusy] = useState(false);
  const [teaser, setTeaser] = useState(false);
  const explain = async () => {
    setBusy(true);
    try { const r = await endpoints.explainMatch(dealId, m.investor_id); setExpl(r.data.text); } catch (e) { setExpl("Error: " + apiError(e)); } finally { setBusy(false); }
  };
  const t = m.ticket || [];
  return (
    <div className="py-3 border-b border-white/5" data-testid={`investor-match-${m.investor_id}`}>
      <div className="flex items-center justify-between gap-3 cursor-pointer" onClick={() => setOpen(!open)}>
        <div className="min-w-0">
          <div className="text-[13px] text-white truncate">{m.investor_name}</div>
          <div className="text-[10.5px] text-white/40">{[m.investor_type, m.stage_focus, m.geo_focus, t[0] ? `${fmt.money(t[0])}–${fmt.money(t[1])}` : null].filter(Boolean).join(" · ")}</div>
        </div>
        <ScoreBar score={m.score} />
      </div>
      {open && <Breakdown b={m.breakdown} />}
      <div className="flex flex-wrap items-center gap-3">
      {expl ? null : (
        <button onClick={explain} disabled={busy} className="mt-2 text-[11px] text-gold/80 hover:text-gold flex items-center gap-1" data-testid={`investor-match-explain-${m.investor_id}`}>
          {busy ? <Spinner size={10} /> : <Sparkles size={11} />} {busy ? "Asking Clara…" : "AI explanation"}
        </button>
      )}
        <button onClick={() => setTeaser(true)} className="mt-2 text-[11px] text-gold/80 hover:text-gold flex items-center gap-1" data-testid={`investor-match-teaser-${m.investor_id}`}>
          <Mail size={11} /> Draft teaser
        </button>
      </div>
      {teaser && <TeaserModal dealId={dealId} investorId={m.investor_id} investorName={m.investor_name} onClose={() => setTeaser(false)} />}
      {expl ? (
        <div className="mt-2 p-2.5 rounded text-[12px] text-white/75 leading-relaxed border border-[#C5A059]/20 bg-[#C5A059]/[0.04]" data-testid={`investor-match-expl-${m.investor_id}`}>{expl}</div>
      ) : null}
    </div>
  );
}

export function InvestorMatchPanel({ deal, onClose }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { endpoints.dealInvestorMatches(deal.id, 10).then((r) => setD(r.data)).catch((e) => setErr(apiError(e))); }, [deal.id]);
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/60 backdrop-blur-sm" onClick={onClose} data-testid="investor-match-overlay">
      <aside onClick={(e) => e.stopPropagation()} className="w-full max-w-lg h-full overflow-y-auto bg-[#0B1120] border-l border-white/10 p-6" data-testid="investor-match-panel">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="overline text-[9px] text-gold mb-1">Investor Match Score</div>
            <div className="font-display text-2xl">{deal.company_name}</div>
            {d && <div className="text-[11.5px] text-white/45">{fmt.money(d.deal.value)} · {d.deal.sector || "—"} · {d.deal.country || "—"} · {d.investors_scored} investors scored</div>}
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white" data-testid="investor-match-close"><X size={18} /></button>
        </div>
        <div className="text-[10.5px] text-white/35 mb-3">Rule-based: sector 35 · ticket 30 · geography 20 · stage 15. Click a row for the breakdown.</div>
        {err && <div className="text-red-300 text-sm">{err}</div>}
        {!d && !err && <Spinner />}
        {d && !d.matches.length && <div className="text-white/40 text-sm" data-testid="investor-match-empty">No investors in your workspace yet — run an Investor Search in Origin.</div>}
        {d?.matches.map((m) => <MatchRow key={m.investor_id} m={m} dealId={deal.id} />)}
      </aside>
    </div>
  );
}

export function InvestorDealMatches({ investorId }) {
  const [d, setD] = useState(null);
  useEffect(() => { endpoints.investorDealMatches(investorId, 8).then((r) => setD(r.data)).catch(() => setD({ matches: [] })); }, [investorId]);
  if (!d) return <Spinner />;
  return (
    <div data-testid="investor-best-deals">
      {!d.matches.length && <div className="text-[12px] text-white/40">No active deals to match.</div>}
      {d.matches.map((m) => (
        <div key={m.deal_id} className="py-2 border-b border-white/5" data-testid={`best-deal-${m.deal_id}`}>
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="text-[12.5px] text-white truncate">{m.company_name}</div>
              <div className="text-[10.5px] text-white/40">{fmt.money(m.value)} · {m.stage} · {m.sector || "—"}</div>
            </div>
            <ScoreBar score={m.score} />
          </div>
          <Badge tone="neutral" className="mt-1 !normal-case !tracking-normal">{m.breakdown.ticket.reason}</Badge>
        </div>
      ))}
    </div>
  );
}
